'use client';

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { tenantStore } from '@/lib/tenantStore';
import { handleSignOut } from '@/lib/auth-client';

interface TenantContextType {
  selectedTenant: string | null;
  availableTenants: string[];
  setSelectedTenant: (tenant: string) => void;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const [selectedTenant, setSelectedTenantState] = useState<string | null>(null);
  const [availableTenants, setAvailableTenants] = useState<string[]>([]);
  const hasRedirected = useRef(false);
  const isLoading = status === 'loading';

  useEffect(() => {
    if (session?.user?.tenants) {
      const tenants = session.user.tenants;

      if (tenants.length === 0) {
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          setAvailableTenants([]);
          setSelectedTenantState(null);
          handleSignOut('/assetcast/auth/signin?error=no_tenant_access');
        }
        return;
      }

      hasRedirected.current = false;
      setAvailableTenants(tenants);

      const storedTenant = tenantStore.getTenant();
      const DEFAULT_TENANT = 'demoisland';
      const tenantToUse = storedTenant && tenants.includes(storedTenant)
        ? storedTenant
        : tenants.includes(DEFAULT_TENANT)
          ? DEFAULT_TENANT
          : tenants[0];

      setSelectedTenantState((current) => {
        if (tenantToUse !== current) {
          tenantStore.setTenant(tenantToUse);
          return tenantToUse;
        }
        return current;
      });
    } else if (status === 'unauthenticated') {
      hasRedirected.current = false;
      setAvailableTenants([]);
      setSelectedTenantState(null);
      tenantStore.clearTenant();
    }
  }, [session, status]);

  const setSelectedTenant = (tenant: string) => {
    if (availableTenants.includes(tenant)) {
      setSelectedTenantState(tenant);
      tenantStore.setTenant(tenant);
    }
  };

  return (
    <TenantContext.Provider
      value={{
        selectedTenant,
        availableTenants,
        setSelectedTenant,
        isLoading,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
